import React, { useState, useEffect } from "react";
import {
  Save, Loader2, Check, Activity, RefreshCw, Clock, MapPin, ChevronDown,
  Gauge, Trophy, Flame, Heart, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { SPORTS, INTENSITY } from "@/lib/planModel";
import { SportIcon } from "@/components/wizard/icons";
import { fmtDuration, fmtDistanceFromMi } from "@/lib/format";

// Format a note's date field ("2026-08-19") for human display in the note
// pinned card. Falls back to the raw string if it can't parse.
function fmtNoteDay(iso) {
  if (!iso) return "today";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d0 = new Date(d); d0.setHours(0, 0, 0, 0);
    if (d0.getTime() === today.getTime()) return "today";
    return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
  } catch { return iso; }
}

// Same square-CTA pattern as Tracker's Section — solid accent square with a
// black icon, keeps the whole Tracker page visually consistent (Feb 26 2026).
function CollapseCard({ title, subtitle, accent = "#4C9AFF", icon: Icon, right, testid, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 bg-[#141414]" data-testid={testid}>
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 px-4 sm:px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
        {Icon && (
          <span className="grid place-items-center h-12 w-12 shrink-0" style={{ backgroundColor: accent, boxShadow: `0 8px 24px -14px ${accent}` }}>
            <Icon className="h-6 w-6 text-black" strokeWidth={2.2} />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold uppercase tracking-[0.2em] text-white truncate">{title}</span>
          {subtitle && <span className="block text-xs text-zinc-300 mt-1 leading-snug break-words">{subtitle}</span>}
        </span>
        {right ? <span className="shrink-0">{right}</span> : null}
        <ChevronDown className={`h-4 w-4 text-zinc-300 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 sm:px-5 pb-4 sm:pb-5">{children}</div>}
    </div>
  );
}

const SPORT_LABEL = Object.fromEntries(SPORTS.map((s) => [s.value, s.label]));
const INTENSITY_LABEL = Object.fromEntries(INTENSITY.map((i) => [i.value, i.label]));
const QUICK_TAGS = ["Fuelling on point", "Under-fuelled", "Travel", "Eating out", "Low appetite", "Big appetite"];

const sportFromText = (t = "") => {
  const s = t.toLowerCase();
  if (/run|jog|5k|10k|marathon/.test(s)) return "run";
  if (/bike|cycl|ride|zwift|mtb/.test(s)) return "cycle";
  if (/swim/.test(s)) return "swim";
  if (/walk|hike/.test(s)) return "walk";
  if (/strength|gym|core|yoga|pilates|lift/.test(s)) return "strength";
  if (/tri|brick/.test(s)) return "triathlon";
  return "other";
};
// Standard: 1 = bad (red) → 5 = good (green). Reverse metrics invert the level.

// -------- Segmented meter (fills 1..value with the value's colour) --------
export function Segments({ value, max, color, onPick, testid, compact }) {
  return (
    <div className="flex gap-0.5" data-testid={testid}>
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
        const on = n <= (value || 0);
        return (
          <button key={n} type="button" data-testid={`${testid}-${n}`} title={String(n)}
            onClick={() => onPick(value === n ? 0 : n)}
            className={`${compact ? "h-6 w-6 text-[9px]" : "h-8 flex-1 text-[10px]"} grid place-items-center border font-mono transition-colors ${on ? "border-transparent text-black font-bold" : "border-white/10 text-zinc-300 hover:border-white/30"}`}
            style={on ? { backgroundColor: color(n) } : undefined}>
            {n}
          </button>
        );
      })}
    </div>
  );
}

// One tappable end-of-day confirmation row.
function ConfirmRow({ icon: Icon, label, hint, on, onToggle, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onToggle}
      className={`w-full flex items-center gap-3 border px-3 py-2.5 text-left transition-colors ${on ? "border-[#34C759]/60 bg-[#34C759]/10" : "border-white/10 hover:border-white/30"}`}>
      <span className={`grid place-items-center h-8 w-8 shrink-0 border ${on ? "border-[#34C759]/50 text-[#34C759]" : "border-white/15 text-zinc-400"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-semibold ${on ? "text-[#34C759]" : "text-white"}`}>{label}</span>
        <span className="block text-[10px] text-zinc-400 mt-0.5">{hint}</span>
      </span>
      <span className={`grid place-items-center h-5 w-5 border shrink-0 ${on ? "bg-[#34C759] border-[#34C759] text-black" : "border-white/25 text-transparent"}`}>
        <Check className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

function StatusPill({ done }) {
  return (
    <span data-testid="session-status"
      className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] border font-bold ${done ? "border-[#34C759] bg-[#34C759] text-black" : "border-white/20 text-zinc-400"}`}>
      {done ? <><Check className="h-3 w-3" /> Done</> : "Planned"}
    </span>
  );
}

// Format pace min/mi → "5:42/mi" or with km fallback.
function fmtPacePerMi(paceMinPerMi, unitSystem) {
  if (!paceMinPerMi || paceMinPerMi <= 0) return null;
  if (unitSystem === "metric") {
    const paceKm = paceMinPerMi / 1.609344;
    const min = Math.floor(paceKm);
    const sec = Math.round((paceKm - min) * 60);
    return `${min}:${String(sec).padStart(2, "0")}/km`;
  }
  const min = Math.floor(paceMinPerMi);
  const sec = Math.round((paceMinPerMi - min) * 60);
  return `${min}:${String(sec).padStart(2, "0")}/mi`;
}

// MET table for a quick calorie-burn estimate when TrainingPeaks doesn't
// include a "Calories:" label in its ICS feed (many TP athlete accounts
// don't). kcal = MET × weight_kg × hours. Values match the industry MET
// compendium for moderate-intensity sessions.
import { estimateKcal, tpHasActuals, tpSessionDone as tpDone } from "@/lib/kcalEstimator";
// Kept locally for backwards-compat with existing code below; both point
// at the shared source of truth in @/lib/kcalEstimator.

// One planned session — collapsed summary that expands to a logging form.
function SessionCard({ item, feedback, setFb, index, unitSystem, weightKg }) {
  const [open, setOpen] = useState(false);
  const done = item.autoDone || !!feedback.completed;
  const distanceLabel = fmtDistanceFromMi(item.plannedMi, unitSystem);
  const actualDistanceLabel = item.tpDistanceMi ? fmtDistanceFromMi(item.tpDistanceMi, unitSystem) : null;
  const actualDurLabel = item.tpDurationMin ? fmtDuration(item.tpDurationMin) : null;
  const paceLabel = fmtPacePerMi(item.tpPaceMinPerMi, unitSystem);
  // TP-reported kcal wins; otherwise fall back to a MET × weight × time
  // estimate so athletes see something instead of "—" when TP omits calories.
  const tpKcal = item.tpCaloriesBurned || null;
  const estKcal = !tpKcal && done ? estimateKcal({
    sport: item.sport,
    durationMin: item.tpDurationMin || item.plannedMin,
    weightKg,
  }) : null;
  const caloriesBurned = tpKcal || estKcal;
  const kcalIsEstimate = !tpKcal && !!estKcal;
  const avgHr = item.tpAvgHr || null;
  const tss = item.tpTss || null;
  // "Live from TP" is only honest when TrainingPeaks actually reported
  // something. A session whose only figure is our own MET estimate is shown
  // as an estimate instead, and empty metrics never render a blank tile.
  const hasTpActuals = !!(actualDistanceLabel || actualDurLabel || paceLabel || tpKcal || avgHr || tss);
  const tiles = [
    actualDistanceLabel && { key: "distance", label: "Distance", value: actualDistanceLabel,
      Icon: MapPin, colour: "#34C759", testId: `journal-session-distance-actual-${index}` },
    actualDurLabel && { key: "time", label: "Time", value: actualDurLabel,
      Icon: Clock, colour: "#4C9AFF", testId: `journal-session-time-actual-${index}` },
    paceLabel && { key: "pace", label: "Pace", value: paceLabel,
      Icon: Gauge, colour: "#E2E30A", testId: `journal-session-pace-${index}` },
    caloriesBurned && { key: "kcal", label: kcalIsEstimate ? "Kcal est." : "Kcal burnt",
      value: kcalIsEstimate ? `~${caloriesBurned}` : `${caloriesBurned}`,
      Icon: Flame, colour: "#FFB454", testId: `journal-session-calories-${index}`,
      title: kcalIsEstimate ? "Estimated — TrainingPeaks didn't include a Calories label on this session." : "Reported by TrainingPeaks" },
    avgHr && { key: "hr", label: "Avg HR", value: `${avgHr}`, suffix: "bpm",
      Icon: Heart, colour: "#FF3B30", testId: `journal-session-hr-${index}` },
    tss && { key: "tss", label: "Training Stress", value: `${tss}`, suffix: "TSS",
      Icon: Zap, colour: "#4C9AFF", testId: `journal-session-tss-${index}` },
  ].filter(Boolean);
  const tileCols = Math.min(tiles.length, 4) || 1;
  return (
    <div data-testid={`journal-session-${index}`}
      className={`border border-white/10 bg-[#141414] transition-colors hover:bg-white/5`}
      style={{ borderLeftWidth: 4, borderLeftColor: done ? "#34C759" : "rgba(255,255,255,0.2)" }}>
      <button type="button" data-testid={`journal-session-toggle-${index}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-3 text-left">
        <span className={`grid place-items-center h-9 w-9 shrink-0 border ${done ? "border-[#34C759]/40 text-[#34C759]" : "border-white/15 text-[#E2E30A]"}`}>
          <SportIcon sport={item.sport} className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-semibold truncate ${done ? "text-[#34C759]" : "text-white"}`}>{item.title}</span>
          {item.tpTitle && item.tpTitle.toLowerCase() !== item.title.toLowerCase() && (
            <span data-testid={`journal-session-tp-title-${index}`}
              className="block text-[11px] text-zinc-300 truncate mt-0.5">
              {item.tpTitle}
            </span>
          )}
          <span className="flex flex-wrap gap-x-2.5 gap-y-0.5 mt-0.5 text-[10px] font-mono uppercase tracking-wider text-zinc-400">
            {!hasTpActuals && item.plannedMin ? <span data-testid={`journal-session-duration-${index}`}><Clock className="h-3 w-3 inline -mt-0.5 mr-0.5" />{fmtDuration(item.plannedMin)}</span> : null}
            {!hasTpActuals && distanceLabel ? <span data-testid={`journal-session-distance-${index}`}><MapPin className="h-3 w-3 inline -mt-0.5 mr-0.5" />{distanceLabel}</span> : null}
            {item.intensity ? <span className="text-[#4C9AFF]">{item.intensity}</span> : null}
            {item.timing ? <span>{item.timing}</span> : null}
          </span>
        </span>
        <StatusPill done={done} />
        <ChevronDown className={`h-4 w-4 text-zinc-300 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Garmin-style actuals — hero-scale numbers for the completed session
          so the athlete's split reads at a glance. Layered gradient + glow
          give the strip a proper "achievement" feel. */}
      {done && hasTpActuals && tiles.length > 0 && (
        <div data-testid={`journal-session-actuals-${index}`}
          className="relative border-t border-[#34C759]/25 bg-gradient-to-b from-[#34C759]/[0.10] via-[#34C759]/[0.05] to-transparent px-4 py-3.5 overflow-hidden">
          <div className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 h-32 w-56 rounded-full bg-[#34C759]/15 blur-3xl" />
          <div className="relative flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5 text-[#34C759]" style={{ filter: "drop-shadow(0 0 6px #34C75988)" }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#34C759]">Completed Activity</span>
            </div>
            <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">Live from TP</span>
          </div>
          <div className="relative grid gap-1 items-stretch"
            style={{ gridTemplateColumns: `repeat(${tileCols}, minmax(0, 1fr))` }}>
            {tiles.map(({ key, label, value, suffix, Icon, colour, testId, title }, i) => (
              <div key={key} data-testid={testId} title={title}
                className={`text-center min-w-0 px-1 py-1 ${i % tileCols ? "border-l border-white/10" : ""}`}
                style={{ background: `${colour}10` }}>
                <Icon className="h-4 w-4 mx-auto mb-1" style={{ color: colour, filter: `drop-shadow(0 0 6px ${colour}88)` }} />
                <p className="font-display text-lg sm:text-2xl font-bold text-white leading-none tabular-nums truncate">
                  {value}{suffix ? <span className="text-xs text-zinc-400 ml-1 font-mono">{suffix}</span> : null}
                </p>
                <p className="text-[9px] uppercase tracking-[0.14em] mt-1" style={{ color: `${colour}cc` }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {open && (
        <div className="border-t border-white/10 px-3 py-3.5 space-y-3.5" data-testid={`journal-session-body-${index}`}>
          {item.focus && <p className="text-xs text-zinc-400 leading-relaxed">{item.focus}</p>}
          {item.target && <p className="text-[10px] uppercase tracking-wider text-zinc-300">Target · <span className="text-zinc-300">{item.target}</span></p>}
          <button type="button" data-testid={`journal-session-done-${index}`}
            onClick={() => setFb(index, { completed: !feedback.completed, manual: !feedback.completed })}
            disabled={item.autoDone}
            className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-[0.15em] border font-bold transition-colors disabled:opacity-70 ${done ? "border-[#34C759] bg-[#34C759] text-black" : "border-white/20 text-zinc-300 hover:border-[#34C759]/60 hover:text-[#34C759]"}`}>
            <Check className="h-3.5 w-3.5" /> {item.autoDone ? "Completed via TrainingPeaks" : (done ? "Completed" : "Mark as done")}
          </button>
        </div>
      )}
    </div>
  );
}

export default function JournalSection({
  note, sessions = [], tpToday = [], tpConnected = false, hasPlan = false,
  updateNote, toggleTag, saveNote, savingNote, noteDirty,
  onRefreshTp, tpRefreshing = false,
  onEditTraining, tpDiff = { changed: false, changedDays: [] }, onTpResync, tpResyncing = false,
  unitSystem = "metric", weightKg = null,
  only = "both",
}) {
  const fb = note.sessionFeedback || {};
  const setFb = (idx, key, val) => updateNote({
    sessionFeedback: {
      ...fb,
      [idx]: { ...(fb[idx] || {}), ...(key && typeof key === "object" ? key : { [key]: val }) },
    },
  });

  // The plan's training week is the single source of truth for BOTH the
  // sessions shown here and the day's macros (TrainingPeaks is imported into the
  // plan at Step 4). Previously this card showed a separate live TP feed, which
  // could differ from the plan and contradicted the macros — confusing. It now
  // always mirrors the plan's sessions.
  // Auto-mark sessions as done when TrainingPeaks reports a completed session
  // on this date with the same sport. Athletes stop having to double-log — if
  // it's ticked in TP, it's ticked here. We also carry the TP-side RPE and
  // post-workout comment through onto each item so the SessionCard can pre-fill
  // the athlete's journal without them retyping what's already in TP.
  const items = (() => {
    if (!hasPlan) {
      // No plan yet — expose the TP-completed sessions directly so athletes on
      // Lite still see what TrainingPeaks logged today.
      return (tpToday || []).map((t, i) => ({
        key: `tp:${i}`, source: "tp", sport: t.sport, title: t.title || SPORT_LABEL[t.sport] || t.sport,
        plannedMin: t.durationMin || null, plannedMi: t.distanceMi || null,
        intensity: null, timing: null, autoDone: tpDone(t),
        tpDataless: !!(t.completed && !tpHasActuals(t)),
        tpRpe: t.completed ? (t.rpe ?? null) : null,
        tpComment: t.completed ? (t.comment || "") : "",
        tpTitle: t.title || "",
        tpDurationMin: t.completed ? (t.durationMin || null) : null,
        tpDistanceMi: t.completed ? (t.distanceMi || null) : null,
        tpPaceMinPerMi: t.completed ? (t.paceMinPerMi || null) : null,
        tpCaloriesBurned: t.completed ? (t.caloriesBurned || null) : null,
        tpAvgHr: t.completed ? (t.avgHr || null) : null,
        tpTss: t.completed ? (t.tss || null) : null,
      }));
    }
    const usedTp = new Set();
    const planItems = sessions.map((s, i) => {
      // Prefer a completed TP session (auto-marks the plan session done).
      let tpIdx = tpToday.findIndex((t, ti) => !usedTp.has(ti) && t.sport === s.sport && t.completed);
      let tp = tpIdx >= 0 ? tpToday[tpIdx] : null;
      // Fallback: still pull TP title from any same-sport TP session so the
      // athlete sees the workout name TP gave it (e.g. "Zone 2 Long Run").
      if (!tp) {
        const anyIdx = tpToday.findIndex((t, ti) => !usedTp.has(ti) && t.sport === s.sport);
        if (anyIdx >= 0) { tpIdx = anyIdx; tp = tpToday[anyIdx]; }
      }
      if (tpIdx >= 0) usedTp.add(tpIdx);
      return {
        key: String(i), source: "plan", sport: s.sport, title: SPORT_LABEL[s.sport] || s.sport,
        plannedMin: s.duration,
        plannedMi: s.tpDistanceMi || tp?.distanceMi || null,
        intensity: INTENSITY_LABEL[s.intensity] || s.intensity,
        timing: s.timing, autoDone: tpDone(tp),
        tpDataless: !!(tp && tp.completed && !tpHasActuals(tp)),
        tpRpe: tp?.completed ? (tp?.rpe ?? null) : null,
        tpComment: tp?.completed ? (tp?.comment || "") : "",
        tpTitle: tp?.title || "",
        tpDurationMin: tp?.completed ? (tp?.durationMin || null) : null,
        tpDistanceMi: tp?.completed ? (tp?.distanceMi || null) : null,
        tpPaceMinPerMi: tp?.completed ? (tp?.paceMinPerMi || null) : null,
        tpCaloriesBurned: tp?.completed ? (tp?.caloriesBurned || null) : null,
        tpAvgHr: tp?.completed ? (tp?.avgHr || null) : null,
        tpTss: tp?.completed ? (tp?.tss || null) : null,
      };
    });
    // Any TP session that didn't match a planned session gets appended so
    // athletes on rest days (or with extra unplanned TP workouts, e.g. a
    // walk on top of the planned run) still see the completed activity
    // with its calories/HR/duration flowing into the day.
    const tpExtras = (tpToday || [])
      .map((t, ti) => ({ t, ti }))
      .filter(({ ti }) => !usedTp.has(ti))
      .map(({ t, ti }) => ({
        key: `tp:${ti}`, source: "tp", sport: t.sport,
        title: t.title || SPORT_LABEL[t.sport] || t.sport,
        plannedMin: t.durationMin || null, plannedMi: t.distanceMi || null,
        intensity: null, timing: null, autoDone: tpDone(t),
        tpDataless: !!(t.completed && !tpHasActuals(t)),
        tpRpe: t.completed ? (t.rpe ?? null) : null,
        tpComment: t.completed ? (t.comment || "") : "",
        tpTitle: t.title || "",
        tpDurationMin: t.completed ? (t.durationMin || null) : null,
        tpDistanceMi: t.completed ? (t.distanceMi || null) : null,
        tpPaceMinPerMi: t.completed ? (t.paceMinPerMi || null) : null,
        tpCaloriesBurned: t.completed ? (t.caloriesBurned || null) : null,
        tpAvgHr: t.completed ? (t.avgHr || null) : null,
        tpTss: t.completed ? (t.tss || null) : null,
      }));
    return [...planItems, ...tpExtras];
  })();

  // Pre-fill athlete feedback from TP-synced values. Runs at most once per
  // unique (key,rpe,comment) tuple so it won't stomp on the athlete's later
  // edits and won't loop the render cycle. Also — crucial for Insights —
  // persists `completed:true` on any autoDone session so the /journal/recap
  // aggregation counts it. Beta feedback (Feb 2026): "Insights still shows
  // 0 sessions ticked for last 7 days" — root cause was autoDone marking
  // the UI but never writing completed:true to day_notes.sessionFeedback.
  const prefilledRef = React.useRef(new Set());
  useEffect(() => {
    let dirty = false;
    const patch = { ...fb };
    items.forEach((it) => {
      // A session only reads as completed when TrainingPeaks reports it done
      // *with data*, or the athlete ticked it themselves (manual flag). This
      // also clears legacy auto-ticks written by an earlier build, which left
      // dataless sessions green with an empty stats strip.
      const stored = patch[it.key] || {};
      if (!it.autoDone) {
        if (stored.completed && !stored.manual) {
          patch[it.key] = { ...stored, completed: false };
          dirty = true;
        }
        return;
      }
      const sig = `${it.key}|${it.tpRpe ?? ""}|${it.tpComment}`;
      if (prefilledRef.current.has(sig)) return;
      prefilledRef.current.add(sig);
      const existing = patch[it.key] || {};
      const upd = {};
      if (!existing.rpe && it.tpRpe != null) upd.rpe = it.tpRpe;
      if (!existing.comment && it.tpComment) upd.comment = it.tpComment;
      // Persist the tick so /journal/recap sees the session as completed.
      if (!existing.completed) upd.completed = true;
      if (Object.keys(upd).length) {
        patch[it.key] = { ...existing, ...upd };
        dirty = true;
      }
    });
    if (dirty) updateNote({ sessionFeedback: patch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => `${i.key}|${i.autoDone ? 1 : 0}|${i.tpDataless ? 1 : 0}|${i.tpRpe ?? ""}|${i.tpComment}`).join("~")]);

  const doneCount = items.filter((it) => it.autoDone || (fb[it.key] || {}).completed).length;
  const totalMin = items.reduce((m, it) => m + (it.plannedMin || 0), 0);

  return (
    <section data-testid="journal-section" className="space-y-5">
      {/* ---------------- TRAINING ---------------- */}
      {only !== "morningReport" && (
      <CollapseCard title="Training" subtitle="Today's sessions and the fuel they earn you" icon={Activity} accent="#E2E30A" testid="training-card"
        right={items.length > 0 ? (
          <span data-testid="training-progress" className="text-[11px] font-mono font-bold uppercase tracking-wider text-zinc-400">
            <span className={doneCount === items.length ? "text-[#34C759]" : "text-white"}>{doneCount}/{items.length}</span> done · {fmtDuration(totalMin)}
          </span>
        ) : null}>
        {items.length === 0 ? (
          <p className="text-sm text-zinc-400" data-testid="training-empty">
            {hasPlan ? "Rest day — no planned sessions." : "No plan covers this date."}
          </p>
        ) : (
          <>
            <div className="space-y-2" data-testid="training-sessions">
              {items.map((it, i) => (
                <SessionCard key={it.key} item={it} index={it.key} feedback={fb[it.key] || {}} setFb={setFb} unitSystem={unitSystem} weightKg={weightKg} />
              ))}
            </div>
            {tpConnected && tpDiff?.changed && onTpResync && (
              <div className="mt-3 border-l-2 border-[#FF9500] bg-[#FF9500]/10 px-3 py-3" data-testid="tp-diff-banner">
                <p className="text-[11px] uppercase tracking-wider text-[#FFB454] font-bold">TrainingPeaks changed</p>
                <p className="text-xs text-zinc-100 mt-1 leading-snug">
                  {tpDiff.changedDays.length} day{tpDiff.changedDays.length > 1 ? "s" : ""} differ from your saved plan. Re-import to recalc your daily intake automatically.
                </p>
                <button type="button" data-testid="tp-resync-btn" onClick={onTpResync} disabled={tpResyncing}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-black bg-[#FF9500] hover:bg-[#e08600] disabled:opacity-60 px-3 py-1.5 font-bold transition-colors">
                  {tpResyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  {tpResyncing ? "Re-importing…" : "Re-import & recalc"}
                </button>
              </div>
            )}
            {tpConnected && !tpDiff?.changed && onEditTraining && (
              <div className="mt-3 border border-[#4C9AFF]/30 bg-[#4C9AFF]/[0.06] px-3 py-2.5 flex items-start gap-2.5" data-testid="tp-sync-callout">
                <RefreshCw className="h-3.5 w-3.5 text-[#4C9AFF] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-zinc-200 leading-snug">
                    <b className="text-[#4C9AFF]">Training matches TrainingPeaks.</b> Change your calendar and this banner will offer a one-tap re-import.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CollapseCard>
      )}

      {/* ---------------- CHAT PREVIEW ---------------- */}
      {/* Chat preview lives at the very top of the Tracker page (moved out
          of JournalSection so it sits above the daily hero) — nothing to
          render here anymore. */}
      {null}

      {/* ---------------- DATE-PINNED NOTES ---------------- */}

      {/* ---------------- MORNING REPORT ---------------- */}
      {only !== "training" && (
      <div className="space-y-2" data-testid="journal-notes">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Fuelling notes</p>
        <div className="flex flex-wrap gap-2">
          {QUICK_TAGS.map((t) => {
            const on = (note.tags || []).includes(t);
            return (
              <button key={t} data-testid={`cal-tag-${t}`} onClick={() => toggleTag(t)}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border transition-colors ${on ? "bg-[#4C9AFF]/10 text-[#4C9AFF] border-[#4C9AFF]" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}>{t}</button>
            );
          })}
        </div>

        <Textarea data-testid="cal-notes-input" value={note.notes || ""}
          onChange={(e) => updateNote({ notes: e.target.value })}
          placeholder="Anything about today's food — appetite, meals out, what worked…"
          className="mt-2 rounded-none bg-[#0A0A0A] border-white/10 text-white min-h-[96px] focus-visible:ring-0 focus-visible:border-[#4C9AFF]" />

        <div className="flex items-center justify-end gap-3 mt-3">
          {noteDirty && <span className="text-[10px] uppercase tracking-wider text-[#E2E30A]">Unsaved changes</span>}
          <Button data-testid="cal-save-note" onClick={saveNote} disabled={savingNote || !noteDirty}
            className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-bold tracking-wider px-6">
            {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save notes</>}
          </Button>
        </div>
      </div>
      )}
    </section>
  );
}
